import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { type JSX, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { DEFAULT_TAB, StatusTabValue } from '~components/Filters/OrdersStatusFilter';
import { CommonLoader } from '~components/Loaders/CommonLoader';
import Tabs, { TabProps } from '~components/Tabs/Tabs';
import { DispatchURLParams, OrdersDispatchStorageKeys } from '~constants/dispatch';
import useActiveHeartbeat from '~hooks/useActiveHeartbeat/useActiveHeartbeat';
import { AnalyticsController } from '~hooks/useAnalytics/AnalyticsController';
import DriversView from '~pages/Dispatch/components/drivers/DriversView';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { DispatchView } from './components/constants';
import { IDispatchViewComponent } from './components/IDispatchViewComponent';
import OrdersView from './components/OrdersView';

const AVAILABLE_VIEWS: TabProps<DispatchView>[] = [
  { label: t('dispatch.dispatch_v2.orders'), value: DispatchView.ORDERS },
  { label: t('dispatch.dispatch_v2.drivers'), value: DispatchView.DRIVERS },
];
const AVAILABLE_VIEW_VALUES = Object.values(DispatchView);
const DEFAULT_VIEW = DispatchView.ORDERS;

const isValidDispatchView = (view: string): view is DispatchView => {
  return AVAILABLE_VIEW_VALUES.includes(view as DispatchView);
};

const getInitialView = (searchParams: URLSearchParams): DispatchView => {
  const viewParam = searchParams.get(DispatchURLParams.VIEW);
  const storedView = localStorage.getItem(OrdersDispatchStorageKeys.VIEW_PARAM);
  const view = viewParam || storedView || '';

  return isValidDispatchView(view) ? view : DEFAULT_VIEW;
};

const getInitialStatusTab = (searchParams: URLSearchParams) => {
  const tabParam = searchParams.get(DispatchURLParams.TAB) as StatusTabValue;
  const storedTab = localStorage.getItem(
    OrdersDispatchStorageKeys.TAB_PARAM,
  ) as StatusTabValue;
  const tab = tabParam || storedTab;

  return Object.values(StatusTabValue).includes(tab) ? tab : DEFAULT_TAB;
};

function DispatchProvider() {
  const { ordersDispatchStore, userStore } = useStores();
  const [searchParams, setSearchParams] = useSearchParams();

  const viewParam = searchParams.get(DispatchURLParams.VIEW) || '';
  const isValidView = isValidDispatchView(viewParam);
  const isOrdersView = viewParam === DispatchView.ORDERS;

  useActiveHeartbeat({
    company_id: getEffectiveUserCompanyId(userStore),
    page: 'dispatch',
    tab: isOrdersView ? searchParams.get(DispatchURLParams.TAB) : undefined,
    view: viewParam,
  });

  const isLoading = !isValidView;

  const handleSelectedViewChange = useCallback(
    (view: DispatchView) => {
      setSearchParams((params) => {
        params.set(DispatchURLParams.VIEW, view);

        return params;
      });
    },
    [setSearchParams],
  );

  useEffect(() => {
    return () => {
      ordersDispatchStore.clearDeepLink();
    };
  }, []);

  useEffect(() => {
    if (isValidView) {
      localStorage.setItem(OrdersDispatchStorageKeys.VIEW_PARAM, viewParam);
      AnalyticsController.track('dispatch_tab_viewed', { tab: viewParam });
    }
  }, [isValidView, viewParam]);

  useEffect(() => {
    setSearchParams((params) => {
      params.set(DispatchURLParams.VIEW, getInitialView(params));
      params.set(DispatchURLParams.TAB, getInitialStatusTab(params));

      return params;
    });
  }, [setSearchParams]);

  if (isLoading) {
    return <CommonLoader />;
  }

  return <Dispatch onSelectedViewChange={handleSelectedViewChange} view={viewParam} />;
}

interface DispatchProps {
  onSelectedViewChange: (view: DispatchView) => void;
  view: DispatchView;
}

const viewComponentByDispatchView: Record<
  DispatchView,
  (props: IDispatchViewComponent) => JSX.Element
> = {
  [DispatchView.ORDERS]: OrdersView,
  [DispatchView.DRIVERS]: DriversView,
};

function Dispatch({ onSelectedViewChange, view }: DispatchProps) {
  const theme = useTheme();
  const { userStore } = useStores();
  const View = viewComponentByDispatchView[view];
  const isViewOnlyUserViewingOrders =
    userStore.isViewOnlyUser && view === DispatchView.ORDERS;

  return (
    <Box
      sx={{
        bgcolor: theme.brandV2.colors.treadGray8,
        display: 'grid',
        gridAutoRows: 'auto auto 1fr',
        gridTemplateColumns: isViewOnlyUserViewingOrders ? '1fr' : 'auto 1fr',
        height: '100%',
        mx: -5,
        width: `calc(100% + ${theme.spacing(5 * 2)})`,
      }}
    >
      <View
        viewSelector={
          <Tabs onChange={onSelectedViewChange} selected={view} tabs={AVAILABLE_VIEWS} />
        }
      />
    </Box>
  );
}

export default observer(DispatchProvider);
